import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import PageHeader from '../components/PageHeader';
import {
  Download,
  FileText,
  User,
  Clock,
  Calendar,
  Filter,
  DollarSign,
  AlertTriangle,
  Lock,
  Send,
  RefreshCw,
  Undo2,
  CheckCircle2,
} from 'lucide-react';
import timesheetApprovalService, {
  type TimesheetApprovalRequest,
  type PayrollPreviewResponse,
  type PayrollStatus,
} from '../services/timesheetApproval';
import payrollPostingService, { type PayrollProviderInfo } from '../services/payrollPosting';
import timesheetLockService from '../services/timesheetLock';
import organizationService from '../services/organization';
import myobService, { type MyobStatus } from '../services/myob';
import { useModuleLicense } from '../hooks/useModuleLicense';
import { useOrgTimezone, formatDateInTz, getTodayInTz, getFirstDayOfMonthInTz, getCurrentPayPeriod } from '../utils/timezone';
import PayPeriodNavigator from '../components/timesheet/PayPeriodNavigator';
import { addDays, stepPayPeriod, type PayPeriod, type PayPeriodMode } from '../utils/payPeriod';
import { toNumOr } from '@/utils/numeric';

// What one person's row shows, from the payroll state of their submissions.
type PersonState = 'awaiting' | 'sending' | 'post_failed' | 'posted' | 'completed';

const personState = (subs: TimesheetApprovalRequest[]): PersonState => {
  const st = subs.map(s => s.payroll_status ?? null);
  if (st.includes('post_failed')) return 'post_failed';
  if (st.includes(null)) return 'awaiting';
  if (st.some(x => x === 'payroll_approved' || x === 'posting')) return 'sending';
  if (st.every(x => x === 'completed')) return 'completed';
  return 'posted';
};

const PERSON_BADGE: Record<PersonState, { label: (p: string) => string; className: string }> = {
  awaiting: { label: () => 'Awaiting payroll', className: 'bg-amber-100 text-amber-800' },
  sending: { label: p => `Sending to ${p}…`, className: 'bg-blue-100 text-blue-800' },
  post_failed: { label: p => `${p} refused it`, className: 'bg-red-100 text-red-800' },
  posted: { label: p => `In ${p} — awaiting pay run`, className: 'bg-indigo-100 text-indigo-800' },
  completed: { label: () => 'Paid', className: 'bg-emerald-100 text-emerald-800' },
};

const SUBMISSION_LABEL: Record<PayrollStatus | 'none', string> = {
  none: 'Awaiting payroll',
  payroll_approved: 'Queued to send',
  posting: 'Sending…',
  posted: 'Sent',
  post_failed: 'Refused',
  completed: 'Paid',
};

function PayrollManager() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const orgTimezone = useOrgTimezone();
  const { hasAccess: hasPayrollLicense, loading: payrollAccessLoading } = useModuleLicense('timesheet', 'payroll');

  const [startDate, setStartDate] = useState(() => getFirstDayOfMonthInTz(orgTimezone));
  const [endDate, setEndDate] = useState(() => getTodayInTz(orgTimezone));
  // The period picker doubles as the employee filter: a weekly cycle only
  // means anything for weekly-paid staff. 'custom' is an arbitrary window
  // across everyone, which is what an out-of-cycle export needs.
  const [mode, setMode] = useState<PayPeriodMode>('custom');
  const payFrequency = mode === 'custom' ? '' : mode;
  const [payCycleStartDay, setPayCycleStartDay] = useState<number>(4);
  const [fortnightlyRefDate, setFortnightlyRefDate] = useState<string | null>(null);
  const [timesheets, setTimesheets] = useState<TimesheetApprovalRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingXero, setExportingXero] = useState(false);
  const [uploadingApi, setUploadingApi] = useState(false);
  const [apiResult, setApiResult] = useState<{ posted: number; failed: Array<{ employee: string; reason: string }>; total: number } | null>(null);
  const [myobStatus, setMyobStatus] = useState<MyobStatus | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<PayrollPreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // After a successful export, offer to lock the exported periods.
  const [lockOffer, setLockOffer] = useState<string | null>(null);
  const [lockingAll, setLockingAll] = useState(false);
  const [lockResult, setLockResult] = useState<string | null>(null);
  // Keyed by user_id — preview / submissions are now grouped per employee.
  const [expandedPreview, setExpandedPreview] = useState<Record<number, boolean>>({});
  const [expandedSubmissions, setExpandedSubmissions] = useState<Record<number, boolean>>({});

  // Payroll by API: when a payroll system is connected, payroll approves each
  // person here and the approval is sent to it — no file exports.
  const [provider, setProvider] = useState<PayrollProviderInfo | null>(null);
  const [readiness, setReadiness] = useState<Record<number, string[]>>({});
  const [busyUser, setBusyUser] = useState<number | 'all' | 'check' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const apiMode = !!provider?.provider;
  const providerLabel = provider?.label ?? 'payroll';

  // Group approved submissions by employee so a payroll manager sees one card
  // per person rather than one card per (employee, period) pair.
  type EmployeeGroup = {
    user_id: number;
    username?: string;
    first_name?: string | null;
    last_name?: string | null;
    email?: string;
    pay_frequency?: 'weekly' | 'fortnightly' | null;
    submissions: TimesheetApprovalRequest[];
    total_hours: number;
    total_entries: number;
    earliest_start: string;
    latest_end: string;
    latest_approved_at?: string;
  };
  const groupedTimesheets = useMemo<EmployeeGroup[]>(() => {
    const byUser = new Map<number, EmployeeGroup>();
    for (const ts of timesheets) {
      const hours = toNumOr(ts.total_hours);
      const existing = byUser.get(ts.user_id);
      if (existing) {
        existing.submissions.push(ts);
        existing.total_hours += hours;
        existing.total_entries += ts.timesheet_entries_count;
        if (ts.start_date < existing.earliest_start) existing.earliest_start = ts.start_date;
        if (ts.end_date > existing.latest_end) existing.latest_end = ts.end_date;
        if (ts.approved_at && (!existing.latest_approved_at || ts.approved_at > existing.latest_approved_at)) {
          existing.latest_approved_at = ts.approved_at;
        }
      } else {
        byUser.set(ts.user_id, {
          user_id: ts.user_id,
          username: ts.username,
          first_name: ts.first_name,
          last_name: ts.last_name,
          email: ts.email,
          pay_frequency: ts.pay_frequency,
          submissions: [ts],
          total_hours: hours,
          total_entries: ts.timesheet_entries_count,
          earliest_start: ts.start_date,
          latest_end: ts.end_date,
          latest_approved_at: ts.approved_at,
        });
      }
    }
    // Sort by display name for a predictable card order.
    return Array.from(byUser.values()).sort((a, b) => {
      const aName = ([a.first_name, a.last_name].filter(Boolean).join(' ') || a.username || '').toLowerCase();
      const bName = ([b.first_name, b.last_name].filter(Boolean).join(' ') || b.username || '').toLowerCase();
      return aName.localeCompare(bName);
    });
  }, [timesheets]);

  // Merge each employee's per-submission category previews into a single
  // bag of {category, hours}. Categories with the same name get summed
  // across submissions so the export preview reflects the whole pay run.
  const mergedCategoriesByUser = useMemo(() => {
    const map = new Map<number, { category: string; hours: number }[]>();
    if (!preview) return map;
    for (const p of preview.previews) {
      const list = map.get(p.user_id) ?? [];
      for (const c of p.categories) {
        const hit = list.find(x => x.category === c.category);
        if (hit) hit.hours += toNumOr(c.hours);
        else list.push({ category: c.category, hours: toNumOr(c.hours) });
      }
      map.set(p.user_id, list);
    }
    return map;
  }, [preview]);

  useEffect(() => {
    organizationService.getCurrentOrganization().then(res => {
      if (res.success && res.data) {
        setPayCycleStartDay((res.data as any).pay_cycle_start_day ?? 4);
        const raw = (res.data as any).fortnightly_cycle_reference_date;
        setFortnightlyRefDate(raw ? raw.slice(0, 10) : null);
      }
    });
    // Fetch MYOB connection status so we know whether to enable the
    // direct-API upload option in the export menu.
    myobService.getStatus().then(res => {
      if (res.success && res.data) setMyobStatus(res.data);
    }).catch(() => { /* non-fatal */ });
    payrollPostingService.getProvider().then(res => {
      if (res.success && res.data) setProvider(res.data);
    }).catch(() => { /* non-fatal: falls back to file exports */ });
  }, []);

  /** What would stop each not-yet-sent submission posting (unlinked, unmapped…). */
  const loadReadiness = useCallback(async (list: TimesheetApprovalRequest[]) => {
    const ids = list
      .filter(t => !t.payroll_status || t.payroll_status === 'post_failed')
      .map(t => t.id);
    if (ids.length === 0) { setReadiness({}); return; }
    try {
      const res = await payrollPostingService.readiness(ids);
      if (res.success && res.data) setReadiness(res.data);
    } catch {
      setReadiness({});
    }
  }, []);

  useEffect(() => {
    if (apiMode && loaded) void loadReadiness(timesheets);
  }, [apiMode, loaded, timesheets, loadReadiness]);

  const period = useMemo<PayPeriod>(
    () => ({ startDate, endDate }),
    [startDate, endDate],
  );

  /**
   * The cycle that just finished, which is the one payroll is about to run.
   * Landing on the current (part-finished) cycle would show half a pay run.
   */
  const lastCompleteCycle = useCallback(
    (freq: 'weekly' | 'fortnightly'): PayPeriod => {
      const current = getCurrentPayPeriod(payCycleStartDay, freq, orgTimezone, fortnightlyRefDate);
      const prevEnd = addDays(current.startDate, -1);
      return {
        startDate: addDays(prevEnd, -(freq === 'weekly' ? 6 : 13)),
        endDate: prevEnd,
      };
    },
    [payCycleStartDay, orgTimezone, fortnightlyRefDate],
  );

  const loadPreview = async (range: PayPeriod, freq: string) => {
    if (!range.startDate || !range.endDate) return;
    try {
      setPreviewLoading(true);
      const response = await timesheetApprovalService.getPayrollPreview({
        start_date: range.startDate,
        end_date: range.endDate,
        ...(freq ? { pay_frequency: freq } : {}),
      });
      if (response.success) {
        setPreview(response.data);
      }
    } catch {
      // preview is non-critical; silently swallow
    } finally {
      setPreviewLoading(false);
    }
  };

  /**
   * Stepping a cycle reloads on its own — the arrows would otherwise leave the
   * page showing one period's figures under another period's dates.
   */
  const handleLoad = async (override?: { range: PayPeriod; freq: string }, { keepError = false } = {}) => {
    const range = override?.range ?? period;
    const freq = override?.freq ?? payFrequency;
    if (!range.startDate || !range.endDate) return;
    try {
      setLoading(true);
      if (!keepError) setError(null);
      setPreview(null);
      setLockOffer(null);
      setLockResult(null);
      const response = await timesheetApprovalService.getPayrollApprovedTimesheets({
        start_date: range.startDate,
        end_date: range.endDate,
        ...(freq ? { pay_frequency: freq } : {}),
        ...(user?.organisation_id != null ? { organisation_id: user.organisation_id } : {}),
      });
      if (response.success) {
        setTimesheets(response.data);
        setLoaded(true);
        // Fire preview load in parallel with the rest of render
        void loadPreview(range, freq);
      } else {
        setError('Failed to load timesheets.');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load timesheets.');
    } finally {
      setLoading(false);
    }
  };

  const handleModeChange = (next: PayPeriodMode) => {
    if (next === mode) return;
    setMode(next);
    if (next === 'custom') {
      // Keep the dates on screen; only the employee filter has been widened.
      if (loaded) void handleLoad({ range: period, freq: '' });
      return;
    }
    const cycle = lastCompleteCycle(next);
    setStartDate(cycle.startDate);
    setEndDate(cycle.endDate);
    void handleLoad({ range: cycle, freq: next });
  };

  const handleNavigate = (direction: 'prev' | 'next') => {
    const next = stepPayPeriod(period, mode, direction);
    setStartDate(next.startDate);
    setEndDate(next.endDate);
    void handleLoad({ range: next, freq: payFrequency });
  };

  // After an action: keep its error on screen. Clearing it here is how a
  // refused Un-approve used to vanish with no banner at all.
  const reload = () => handleLoad(undefined, { keepError: true });

  const problemsFor = (subs: TimesheetApprovalRequest[]) =>
    subs.filter(s => !s.payroll_status).flatMap(s => readiness[s.id] ?? []);

  const runAction = async (key: number | 'all' | 'check', fn: () => Promise<string | null>) => {
    setBusyUser(key);
    setError(null);
    setNotice(null);
    try {
      const msg = await fn();
      if (msg) setNotice(msg);
    } catch (err: any) {
      setError(err?.message || 'That did not work.');
    } finally {
      setBusyUser(null);
      await reload();
    }
  };

  const approveIds = (key: number | 'all', ids: number[]) => runAction(key, async () => {
    const res = await payrollPostingService.approve(ids);
    if (!res.success || !res.data) return null;
    const { accepted, skipped } = res.data;
    const parts = [`Approved ${accepted.length} timesheet${accepted.length === 1 ? '' : 's'} and sent to ${providerLabel}.`];
    if (skipped.length) parts.push(`${skipped.length} skipped: ${skipped.map(s => s.reason).join(' ')}`);
    return parts.join(' ');
  });

  const handleApprovePerson = (g: { user_id: number; submissions: TimesheetApprovalRequest[] }) =>
    approveIds(g.user_id, g.submissions.filter(s => !s.payroll_status).map(s => s.id));

  const readyIds = useMemo(
    () => timesheets.filter(t => !t.payroll_status && !(readiness[t.id]?.length)).map(t => t.id),
    [timesheets, readiness]
  );

  const handleApproveAllReady = () => {
    if (readyIds.length === 0) return;
    if (!window.confirm(`Approve ${readyIds.length} timesheet${readyIds.length === 1 ? '' : 's'} and send them to ${providerLabel}?\n\nEach person's pay period is locked once approved.`)) return;
    void approveIds('all', readyIds);
  };

  const handleRetry = (g: { user_id: number; submissions: TimesheetApprovalRequest[] }) => runAction(g.user_id, async () => {
    const ids = g.submissions.filter(s => s.payroll_status === 'post_failed').map(s => s.id);
    await payrollPostingService.retry(ids);
    return null;
  });

  const handleUnapprove = (g: { user_id: number; submissions: TimesheetApprovalRequest[] }) => {
    const ids = g.submissions
      .filter(s => s.payroll_status && s.payroll_status !== 'completed' && s.payroll_status !== 'posting')
      .map(s => s.id);
    if (ids.length === 0) return;
    if (!window.confirm(`Take this back from ${providerLabel} and return it to review?\n\nIt is withdrawn from ${providerLabel} and the pay period is unlocked. This is refused if ${providerLabel} has already paid it.`)) return;
    void runAction(g.user_id, async () => {
      for (const id of ids) await payrollPostingService.unapprove(id);
      return 'Returned to payroll review.';
    });
  };

  const handleMarkPaid = (g: { user_id: number; submissions: TimesheetApprovalRequest[] }) => runAction(g.user_id, async () => {
    const ids = g.submissions.filter(s => s.payroll_status === 'posted').map(s => s.id);
    await payrollPostingService.markCompleted(ids);
    return null;
  });

  const handleCheckCompletion = () => runAction('check', async () => {
    const res = await payrollPostingService.checkCompletion();
    const n = res.success && res.data ? res.data.completed.length : 0;
    return n
      ? `${n} timesheet${n === 1 ? '' : 's'} now paid in ${providerLabel}; leave balances refreshed.`
      : `No new posted pay runs in ${providerLabel} yet.`;
  });

  const handleExport = async () => {
    if (!startDate || !endDate) return;
    try {
      setExporting(true);
      setError(null);
      const blob = await timesheetApprovalService.exportToMYOB({
        start_date: startDate,
        end_date: endDate,
        ...(payFrequency ? { pay_frequency: payFrequency } : {}),
        ...(user?.organisation_id != null ? { organisation_id: user.organisation_id } : {}),
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const today = getTodayInTz(orgTimezone);
      const freqSuffix = payFrequency ? `_${payFrequency}` : '';
      a.download = `myob_payroll_${today}${freqSuffix}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setLockOffer('export:MYOB');
    } catch (err: any) {
      setError(err?.message || 'Export failed.');
    } finally {
      setExporting(false);
    }
  };

  const handleUploadMyobApi = async () => {
    if (!startDate || !endDate) return;
    if (!window.confirm(
      'Upload these timesheets directly to MYOB AccountRight?\n\n' +
      'Lines will appear in your company file ready for the bookkeeper to ' +
      'process the pay run. STP lodgement still happens inside MYOB.'
    )) return;
    try {
      setUploadingApi(true);
      setError(null);
      setApiResult(null);
      const res = await timesheetApprovalService.uploadToMYOBApi({
        start_date: startDate,
        end_date: endDate,
        ...(payFrequency ? { pay_frequency: payFrequency } : {}),
        ...(user?.organisation_id != null ? { organisation_id: user.organisation_id } : {}),
      });
      if (res.success && res.data) {
        setApiResult({ posted: res.data.posted, failed: res.data.failed, total: res.data.total });
        if (res.data.posted > 0) setLockOffer('export:MYOB');
      } else {
        setError('Upload failed.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err?.message || 'MYOB API upload failed.');
    } finally {
      setUploadingApi(false);
    }
  };

  const handleExportXero = async () => {
    if (!startDate || !endDate) return;
    try {
      setExportingXero(true);
      setError(null);
      const blob = await timesheetApprovalService.exportToXero({
        start_date: startDate,
        end_date: endDate,
        ...(payFrequency ? { pay_frequency: payFrequency } : {}),
        ...(user?.organisation_id != null ? { organisation_id: user.organisation_id } : {}),
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const today = getTodayInTz(orgTimezone);
      const freqSuffix = payFrequency ? `_${payFrequency}` : '';
      a.download = `xero_payroll_${today}${freqSuffix}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setLockOffer('export:Xero');
    } catch (err: any) {
      setError(err?.message || 'Xero export failed.');
    } finally {
      setExportingXero(false);
    }
  };

  // Lock every exported employee's period so no further changes can be made.
  const handleLockAllExported = async () => {
    if (groupedTimesheets.length === 0) return;
    setLockingAll(true);
    setError(null);
    let locked = 0;
    let failed = 0;
    for (const g of groupedTimesheets) {
      try {
        const res = await timesheetLockService.lockPeriod({
          user_id: g.user_id,
          period_start: g.earliest_start,
          period_end: g.latest_end,
          reason: lockOffer || 'export',
        });
        if (res.success) locked++;
        else failed++;
      } catch {
        failed++;
      }
    }
    setLockingAll(false);
    setLockOffer(null);
    setLockResult(
      failed === 0
        ? `Locked ${locked} employee pay period${locked !== 1 ? 's' : ''}. Further changes are now blocked until reopened.`
        : `Locked ${locked} period${locked !== 1 ? 's' : ''}; ${failed} failed. Check the Pay Cycle Dashboard.`
    );
  };

  // Auto-reload when org changes (user switches org in page header)
  useEffect(() => {
    if (!user?.organisation_id || !startDate || !endDate) return;
    setTimesheets([]);
    setLoaded(false);
    setError(null);
    setLoading(true);
    setPreview(null);
    timesheetApprovalService.getPayrollApprovedTimesheets({
      start_date: startDate,
      end_date: endDate,
      ...(payFrequency ? { pay_frequency: payFrequency } : {}),
    }).then(response => {
      if (response.success) {
        setTimesheets(response.data);
        setLoaded(true);
        void loadPreview({ startDate, endDate }, payFrequency);
      } else {
        setError('Failed to load timesheets.');
      }
    }).catch((err: any) => {
      setError(err?.message || 'Failed to load timesheets.');
    }).finally(() => {
      setLoading(false);
    });
  }, [user?.organisation_id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (payrollAccessLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-blue-50 py-12 text-center text-neutral-500">
        Loading…
      </div>
    );
  }
  if (!hasPayrollLicense) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-blue-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-lg text-neutral-600">
                A Timesheet Payroll license seat is required to access this page.
              </p>
              <Button onClick={() => navigate('/dashboard')} className="mt-4">
                Return to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-blue-50">
      <PageHeader
        title="Payroll Manager"
        subtitle={apiMode
          ? `Review each person, approve, and send to ${providerLabel}`
          : 'View and export approved timesheets for payroll processing'}
        icon={<DollarSign className="h-6 w-6 text-white" />}
        actionButtons={apiMode ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={handleApproveAllReady}
              disabled={!loaded || loading || busyUser !== null || readyIds.length === 0 || provider?.can_post === false}
              className="flex items-center gap-2"
            >
              <Send className="h-4 w-4" />
              {busyUser === 'all' ? 'Sending…' : `Approve & send ${readyIds.length} ready`}
            </Button>
            {provider?.auto_completes && (
              <Button
                variant="outline"
                onClick={() => void handleCheckCompletion()}
                disabled={busyUser !== null}
                className="flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                {busyUser === 'check' ? 'Checking…' : `Check ${providerLabel} for paid runs`}
              </Button>
            )}
          </div>
        ) : (
          // Three export buttons on one line run off a 320px screen. Wrapping
          // costs a second row on a phone and nothing on a laptop.
          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={handleExport}
              disabled={!loaded || timesheets.length === 0 || exporting || exportingXero || uploadingApi || loading}
              className="flex items-center gap-2"
              title="Download a tab-delimited .txt for manual import in MYOB"
            >
              <Download className="h-4 w-4" />
              {exporting ? 'Exporting...' : 'MYOB (.txt file)'}
            </Button>
            <Button
              onClick={handleUploadMyobApi}
              disabled={
                !loaded || timesheets.length === 0 || exporting || exportingXero || uploadingApi || loading ||
                !myobStatus?.connected || !myobStatus?.company_file_selected
              }
              className="flex items-center gap-2"
              title={
                !myobStatus?.connected
                  ? 'Connect MYOB first under HR → MYOB Integration'
                  : !myobStatus?.company_file_selected
                    ? 'Select a MYOB company file under HR → MYOB Integration'
                    : 'Upload timesheets directly into your MYOB AccountRight company file'
              }
            >
              <Download className="h-4 w-4" />
              {uploadingApi ? 'Uploading...' : 'MYOB (direct API)'}
            </Button>
            {/*
              TODO: When per-org Xero Payroll API is added, split the Xero
              button the same way — keep this CSV download, then add a second
              "Xero (direct API)" button calling timesheetApprovalService
              .uploadToXeroApi() once that exists.
            */}
            <Button
              variant="outline"
              onClick={handleExportXero}
              disabled={!loaded || timesheets.length === 0 || exporting || exportingXero || uploadingApi || loading}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              {exportingXero ? 'Exporting...' : 'Xero (.csv file)'}
            </Button>
          </div>
        )}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Pay period selector */}
        <Card className="mb-6">
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
              <div className="flex items-start gap-2 min-w-0">
                <Filter className="h-5 w-5 text-neutral-500 shrink-0 mt-2" />
                <div className="min-w-0">
                  <PayPeriodNavigator
                    mode={mode}
                    onModeChange={handleModeChange}
                    modes={['weekly', 'fortnightly', 'custom']}
                    period={period}
                    onNavigate={handleNavigate}
                    onPeriodChange={next => {
                      setStartDate(next.startDate);
                      setEndDate(next.endDate);
                    }}
                    disabled={loading}
                  />
                  <p className="text-xs text-neutral-500 mt-2">
                    {mode === 'custom'
                      ? 'All employees, any pay frequency. Submissions whose period falls entirely inside the dates.'
                      : `${mode === 'weekly' ? 'Weekly' : 'Fortnightly'}-paid employees only. Use the arrows to step whole pay cycles.`}
                  </p>
                </div>
              </div>
              <Button
                onClick={() => handleLoad()}
                disabled={!startDate || !endDate || loading}
                className="self-start xl:self-auto shrink-0"
              >
                {loading ? 'Loading...' : 'Load Timesheets'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Error */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        {apiMode && provider?.can_post === false && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900 flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
            <span className="min-w-0">
              The {providerLabel} connection can only read. Reconnect it on the{' '}
              <button type="button" className="underline font-medium" onClick={() => navigate('/accounts/xero')}>
                Xero Payroll settings
              </button>{' '}page to allow timesheets and leave to be sent.
            </span>
          </div>
        )}

        {notice && (
          <div className="mb-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800 flex items-start gap-2">
            <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" />
            <span className="min-w-0 break-words">{notice}</span>
          </div>
        )}

        {/* Offer to lock exported periods */}
        {lockOffer && timesheets.length > 0 && (
          <div className="mb-4 p-4 bg-neutral-800 text-white rounded-lg flex flex-col sm:flex-row sm:items-center gap-3">
            <Lock className="h-5 w-5 shrink-0" />
            <div className="flex-1 text-sm">
              <p className="font-semibold">Export complete — lock these pay periods?</p>
              <p className="text-neutral-300">
                Locking finalises {groupedTimesheets.length} employee period{groupedTimesheets.length !== 1 ? 's' : ''} so no further
                edits, clock-ins, or leave can be recorded against them. HR can reopen a period from the Pay Cycle Dashboard.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button onClick={handleLockAllExported} disabled={lockingAll} className="flex items-center gap-2">
                <Lock className="h-4 w-4" />
                {lockingAll ? 'Locking…' : 'Lock exported periods'}
              </Button>
              <button
                onClick={() => setLockOffer(null)}
                className="px-3 py-2 text-sm text-neutral-300 hover:text-white"
              >
                Not now
              </button>
            </div>
          </div>
        )}

        {/* Lock result */}
        {lockResult && (
          <div className="mb-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800 flex items-center gap-2">
            <Lock className="h-4 w-4 shrink-0" />
            {lockResult}
          </div>
        )}

        {/* MYOB API upload result */}
        {apiResult && (
          <div className={`mb-4 p-4 rounded-lg border text-sm ${
            apiResult.failed.length === 0
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : apiResult.posted > 0
                ? 'bg-amber-50 border-amber-200 text-amber-900'
                : 'bg-red-50 border-red-200 text-red-700'
          }`}>
            <p className="font-semibold mb-1">
              {apiResult.failed.length === 0
                ? `Uploaded ${apiResult.posted} of ${apiResult.total} timesheets to MYOB.`
                : apiResult.posted > 0
                  ? `Uploaded ${apiResult.posted} of ${apiResult.total}. ${apiResult.failed.length} failed:`
                  : `Upload failed for all ${apiResult.failed.length} employees.`}
            </p>
            {apiResult.failed.length > 0 && (
              <ul className="mt-2 space-y-1 list-disc list-inside">
                {apiResult.failed.map((f, i) => (
                  <li key={i}><span className="font-medium">{f.employee}:</span> {f.reason}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Unseeded public holidays warning */}
        {loaded && preview && !previewLoading && !preview.public_holidays_seeded && timesheets.length > 0 && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1 text-sm text-amber-800">
              <p className="font-semibold mb-1">No public holidays configured for this date range</p>
              <p>
                Any public holidays falling inside {formatDateInTz(startDate, orgTimezone)} – {formatDateInTz(endDate, orgTimezone)} will not appear in the MYOB/Xero export. Go to{' '}
                <button
                  type="button"
                  onClick={() => navigate('/admin/public-holidays')}
                  className="underline font-medium hover:text-amber-900"
                >
                  Admin → Public Holidays
                </button>{' '}
                to seed them for your state.
              </p>
            </div>
          </div>
        )}

        {/* Public holidays present in the range */}
        {loaded && preview && preview.public_holidays.length > 0 && (
          <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">
            <span className="font-semibold">Public holidays in this range:</span>{' '}
            {preview.public_holidays.map(h => `${formatDateInTz(`${h.date}T00:00:00Z`, orgTimezone)} (${h.name})`).join(' · ')}
          </div>
        )}

        {/* Results */}
        {loading ? (
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-neutral-600">Loading approved timesheets...</p>
            </CardContent>
          </Card>
        ) : !loaded ? (
          <Card>
            <CardContent className="p-8 text-center">
              <FileText className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
              <p className="text-lg text-neutral-600">Select a date range and click Load Timesheets</p>
              <p className="text-sm text-neutral-500 mt-2">
                Shows all approved timesheet submissions across the organisation.
              </p>
            </CardContent>
          </Card>
        ) : timesheets.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Calendar className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
              <p className="text-lg text-neutral-600">No approved timesheets found</p>
              <p className="text-sm text-neutral-500 mt-2">
                No timesheets were approved between {formatDateInTz(startDate, orgTimezone)} and {formatDateInTz(endDate, orgTimezone)}.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Summary bar */}
            <div className="mb-4 flex items-center justify-between">
              <p className="text-sm text-neutral-600">
                <span className="font-medium text-neutral-900">{groupedTimesheets.length}</span> employee{groupedTimesheets.length !== 1 ? 's' : ''}
                &nbsp;·&nbsp;
                <span className="font-medium text-neutral-900">{timesheets.length}</span> approved submission{timesheets.length !== 1 ? 's' : ''}
                &nbsp;·&nbsp;
                <span className="font-medium text-neutral-900">
                  {timesheets.reduce((sum, t) => sum + (parseFloat(t.total_hours as unknown as string) || 0), 0).toFixed(2)}
                </span> total hours
              </p>
            </div>

            <div className="space-y-4">
              {groupedTimesheets.map((g) => {
                const categories = mergedCategoriesByUser.get(g.user_id) ?? [];
                const isPreviewOpen = !!expandedPreview[g.user_id];
                const isSubsOpen = !!expandedSubmissions[g.user_id];
                const displayName = g.first_name || g.last_name
                  ? [g.first_name, g.last_name].filter(Boolean).join(' ')
                  : g.username;
                return (
                <Card key={g.user_id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      {/* Employee info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <User className="h-5 w-5 text-neutral-500" />
                          <div>
                            <p className="font-semibold text-neutral-900">{displayName}</p>
                            {(g.first_name || g.last_name) && (
                              <p className="text-xs text-neutral-400">@{g.username}</p>
                            )}
                            <p className="text-sm text-neutral-600">{g.email}</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <p className="text-xs text-neutral-500 mb-1">Period</p>
                            <p className="text-sm font-medium text-neutral-900">
                              {formatDateInTz(g.earliest_start, orgTimezone)} – {formatDateInTz(g.latest_end, orgTimezone)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-neutral-500 mb-1">Total Hours</p>
                            <p className="text-sm font-medium text-neutral-900">
                              {g.total_hours.toFixed(2)} h
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-neutral-500 mb-1">Entries</p>
                            <p className="text-sm font-medium text-neutral-900">
                              {g.total_entries}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-neutral-500 mb-1">Submissions</p>
                            <p className="text-sm font-medium text-neutral-900">
                              {g.submissions.length}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Right side */}
                      <div className="flex flex-col items-end gap-2">
                        {apiMode ? (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${PERSON_BADGE[personState(g.submissions)].className}`}>
                            {PERSON_BADGE[personState(g.submissions)].label(providerLabel)}
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Approved
                          </span>
                        )}
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          g.pay_frequency === 'weekly'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {g.pay_frequency === 'weekly' ? 'Weekly' : 'Fortnightly'}
                        </span>
                        {g.latest_approved_at && (
                          <div className="flex items-center gap-1 text-xs text-neutral-500">
                            <Clock className="h-3 w-3" />
                            <span>{formatDateInTz(g.latest_approved_at, orgTimezone)}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {apiMode && (() => {
                      const state = personState(g.submissions);
                      const problems = problemsFor(g.submissions);
                      const errors = g.submissions.filter(s => s.post_error).map(s => s.post_error as string);
                      const busy = busyUser === g.user_id;
                      const canUnapprove = g.submissions.some(s =>
                        s.payroll_status && s.payroll_status !== 'completed' && s.payroll_status !== 'posting');
                      return (
                        <div className="mt-4 pt-4 border-t border-neutral-200 space-y-3">
                          {state === 'awaiting' && problems.length > 0 && (
                            <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-900">
                              <p className="font-medium mb-1">Can't send to {providerLabel} yet:</p>
                              <ul className="list-disc list-inside space-y-0.5">
                                {[...new Set(problems)].map(p => <li key={p} className="break-words">{p}</li>)}
                              </ul>
                            </div>
                          )}
                          {state === 'post_failed' && errors.length > 0 && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800 break-words">
                              {[...new Set(errors)].join(' ')}
                            </div>
                          )}
                          {state === 'sending' && (
                            <p className="text-sm text-neutral-600">
                              Queued — sending to {providerLabel} in the background. Reload in a minute.
                            </p>
                          )}
                          {state === 'posted' && (
                            <p className="text-sm text-neutral-600">
                              {provider?.auto_completes
                                ? `In ${providerLabel}. Finish and post the pay run there (STP is lodged in ${providerLabel}); it shows as Paid here once posted.`
                                : `In ${providerLabel}. Process the pay there, then mark it paid here.`}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-2">
                            {state === 'awaiting' && (
                              <Button
                                onClick={() => void handleApprovePerson(g)}
                                disabled={busyUser !== null || problems.length > 0 || provider?.can_post === false}
                                className="flex items-center gap-2"
                              >
                                <Send className="h-4 w-4" />
                                {busy ? 'Sending…' : `Approve & send to ${providerLabel}`}
                              </Button>
                            )}
                            {state === 'post_failed' && (
                              <Button onClick={() => void handleRetry(g)} disabled={busyUser !== null} className="flex items-center gap-2">
                                <RefreshCw className="h-4 w-4" />
                                {busy ? 'Retrying…' : 'Retry'}
                              </Button>
                            )}
                            {state === 'posted' && !provider?.auto_completes && (
                              <Button onClick={() => void handleMarkPaid(g)} disabled={busyUser !== null} className="flex items-center gap-2">
                                <CheckCircle2 className="h-4 w-4" />
                                Mark paid
                              </Button>
                            )}
                            {canUnapprove && (
                              <Button
                                variant="outline"
                                onClick={() => handleUnapprove(g)}
                                disabled={busyUser !== null}
                                className="flex items-center gap-2"
                              >
                                <Undo2 className="h-4 w-4" />
                                Un-approve
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Per-submission breakdown for this employee */}
                    <div className="mt-4 pt-4 border-t border-neutral-200">
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedSubmissions(prev => ({ ...prev, [g.user_id]: !prev[g.user_id] }))
                        }
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        {isSubsOpen
                          ? 'Hide individual submissions'
                          : `Show ${g.submissions.length} individual submission${g.submissions.length !== 1 ? 's' : ''}`}
                      </button>
                      {isSubsOpen && (
                        <div className="mt-3 overflow-x-auto">
                          <table className="min-w-full text-xs">
                            <thead>
                              <tr className="text-left text-neutral-500">
                                <th className="py-1 pr-4 font-medium">Period</th>
                                <th className="py-1 pr-4 font-medium">Hours</th>
                                <th className="py-1 pr-4 font-medium">Entries</th>
                                <th className="py-1 pr-4 font-medium">Approved By</th>
                                <th className="py-1 pr-4 font-medium">Approved On</th>
                                {apiMode && <th className="py-1 font-medium">Payroll</th>}
                              </tr>
                            </thead>
                            <tbody className="text-neutral-800">
                              {g.submissions
                                .slice()
                                .sort((a, b) => a.start_date.localeCompare(b.start_date))
                                .map(ts => (
                                  <tr key={ts.id} className="border-t border-neutral-100">
                                    <td className="py-1 pr-4">
                                      {formatDateInTz(ts.start_date, orgTimezone)} – {formatDateInTz(ts.end_date, orgTimezone)}
                                    </td>
                                    <td className="py-1 pr-4">
                                      {(parseFloat(ts.total_hours as unknown as string) || 0).toFixed(2)} h
                                    </td>
                                    <td className="py-1 pr-4">{ts.timesheet_entries_count}</td>
                                    <td className="py-1 pr-4">{ts.approved_by_username || '—'}</td>
                                    <td className="py-1 pr-4">{ts.approved_at ? formatDateInTz(ts.approved_at, orgTimezone) : '—'}</td>
                                    {apiMode && <td className="py-1">{SUBMISSION_LABEL[ts.payroll_status ?? 'none']}</td>}
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    {/* Merged export-category preview across this employee's submissions */}
                    {categories.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-neutral-200">
                        <button
                          type="button"
                          onClick={() =>
                            setExpandedPreview(prev => ({ ...prev, [g.user_id]: !prev[g.user_id] }))
                          }
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                        >
                          {isPreviewOpen ? 'Hide export preview' : 'Preview export categories'}
                        </button>
                        {isPreviewOpen && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {categories.map(c => {
                              const isPH = c.category === 'Public Holiday' || c.category === 'Public Holiday Worked';
                              return (
                                <span
                                  key={c.category}
                                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                                    isPH
                                      ? 'bg-emerald-100 text-emerald-800'
                                      : 'bg-neutral-100 text-neutral-700'
                                  }`}
                                >
                                  <span>{c.category}</span>
                                  <span className="font-bold">{c.hours.toFixed(2)}h</span>
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
                );
              })}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default PayrollManager;
